"use client";

import { useEffect } from "react";
import {
  buildWebMcpPreflightSnapshot,
  type WebMcpToolsPolicy,
} from "@/lib/agent/webmcp-preflight";
import type { AppLocale } from "@/lib/i18n/constants";
import { buildAgentReadinessTaskStepPayload } from "@/lib/posthog/agent-readiness";
import { track } from "@/lib/posthog/events";

type WebMcpPreflightProps = {
  readonly locale: AppLocale;
};

type ModelContextLike = {
  readonly registerTool?: unknown;
};

type PermissionsPolicyLike = {
  readonly allowedFeatures?: () => readonly string[];
  readonly allowsFeature?: (feature: string) => boolean;
};

export default function WebMcpPreflight({ locale }: WebMcpPreflightProps) {
  useEffect(() => {
    const modelContext = (document as Document & { modelContext?: ModelContextLike }).modelContext;
    const snapshot = buildWebMcpPreflightSnapshot({
      secureContext: window.isSecureContext,
      topLevelContext: isTopLevelContext(),
      modelContextAvailable: Boolean(modelContext),
      registerToolAvailable: typeof modelContext?.registerTool === "function",
      toolsPolicy: readToolsPolicy(),
    });

    track(
      "agent_readiness_task_step",
      buildAgentReadinessTaskStepPayload({
        agentEngine: navigator.userAgent.toLowerCase().includes("chrome") ? "chrome-webmcp" : "browser-webmcp",
        agentRunId: "webmcp-preflight",
        agentStepId: snapshot.status,
        agentSurface: "site",
        agentTaskId: "webmcp-preflight",
        blockedReason: snapshot.blockedReason,
        commercialImpactBand: "none",
        delegationSurface: "none",
        result:
          snapshot.status === "ready_for_inspector"
            ? "pass"
            : snapshot.status === "blocked"
              ? "blocked"
              : "not_determinable",
        routePath: window.location.pathname || `/${locale}`,
        sourceEventKind: "browser_event",
        trackingSurfaceState: "defined_live",
      }),
    );
  }, [locale]);

  return null;
}

function isTopLevelContext(): boolean {
  try {
    return window.top === window;
  } catch {
    return false;
  }
}

function readToolsPolicy(): WebMcpToolsPolicy {
  const policy = (
    document as Document & {
      featurePolicy?: PermissionsPolicyLike;
      permissionsPolicy?: PermissionsPolicyLike;
    }
  ).permissionsPolicy ??
    (document as Document & { featurePolicy?: PermissionsPolicyLike }).featurePolicy;

  if (!policy?.allowsFeature) {
    return "unknown";
  }

  try {
    const allowedFeatures = policy.allowedFeatures?.() ?? [];

    if (!allowedFeatures.includes("tools")) {
      return "unknown";
    }

    return policy.allowsFeature("tools") ? "allowed" : "blocked";
  } catch {
    return "unknown";
  }
}
